import {
  campaignPeriodStatusSchema,
  campaignPricingModeSchema,
  currencySchema,
  entryLabelOverridesSchema,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { campaignNavigationCategorySchema } from './campaign.js';
import { commonErrors } from './common/errors.js';
import { formSchemaDocumentSchema } from './common/form-schema.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * A campaign as the entry form picks it: what the form needs to ask the right
 * questions and nothing a management screen shows — no owner, no goal, no
 * totals, no readiness or grouping configuration. `activePeriodId` is the
 * season a save lands in, active and inside its window, or null when none is
 * open, so the form can say why the campaign takes nothing today.
 */
export const entryCampaignSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  categoryId: z.uuid(),
  categoryName: z.string(),
  pricingMode: campaignPricingModeSchema,
  unitLabel: z.string().nullable(),
  priceFieldKeys: z.array(z.string()),
  entryLabels: entryLabelOverridesSchema,
  activePeriodId: z.uuid().nullable(),
});

export type EntryCampaign = z.infer<typeof entryCampaignSchema>;

/** The campaign's current published form, as the clerk fills it — who published it is not theirs. */
export const entryFormSchema = z.object({
  campaignId: z.uuid(),
  version: z.number().int().min(0),
  document: formSchemaDocumentSchema,
});

export type EntryForm = z.infer<typeof entryFormSchema>;

/** A currency the season prices, and whether it is the season's default. */
export const entryCurrencySchema = z.object({
  currency: currencySchema,
  isDefault: z.boolean(),
});

export type EntryCurrency = z.infer<typeof entryCurrencySchema>;

export const donationCampaignOptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  categoryId: z.uuid(),
  periods: z.array(
    z.object({
      id: z.uuid(),
      seasonYear: z.number().int().nullable(),
      status: campaignPeriodStatusSchema,
    }),
  ),
});

export const donationCampaignCategoryOptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  icon: z.string().nullable(),
});

/**
 * The campaigns a donation list may be filtered by, with their seasons, and the
 * categories they sit in — the management screens' reach
 * (`campaignManagementPredicate`), because a list of donations names campaigns
 * the reader's institutions collected for, not only the ones their own seat
 * does. Archived campaigns are left out, as the pickers always left them.
 */
export const donationCampaignFilterSchema = z.object({
  categories: z.array(donationCampaignCategoryOptionSchema),
  campaigns: z.array(donationCampaignOptionSchema),
});

export type DonationCampaignFilter = z.infer<typeof donationCampaignFilterSchema>;

/**
 * What a donation screen reads about campaigns — docs/decisions/0088. Each
 * route is guarded by the permission of the screen that reads it and by no
 * campaign permission, so entering, listing, queueing and restoring donations
 * never needs the right to manage a campaign, and the campaign screens'
 * routes (`campaign.*`, `campaignCategory.*`) are management alone. Every one
 * delegates to the campaign module's own service, so its scope predicate is the
 * one the management screens and the save already apply.
 */
export const donationCampaignContract = {
  /** The entry form's campaign picker: what the caller's seat may collect for. */
  entry: oc
    .route({ method: 'GET', path: '/donation-campaigns/entry' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(entryCampaignSchema) })),

  entryForm: oc
    .route({ method: 'GET', path: '/donation-campaigns/entry-form' })
    .errors(guarded)
    .input(z.object({ campaignId: z.uuid() }))
    .output(z.object({ form: entryFormSchema })),

  /** The season's own currencies, never an assignment's override of them. */
  entryCurrencies: oc
    .route({ method: 'GET', path: '/donation-campaigns/entry-currencies' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid() }))
    .output(z.object({ items: z.array(entryCurrencySchema) })),

  /**
   * The donations menu, as data: categories with the campaigns the caller may
   * collect for whose period is `active` **and** inside its window, so a menu
   * entry always leads to a donation list that can take a donation.
   */
  navigation: oc
    .route({ method: 'GET', path: '/donation-campaigns/navigation' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ categories: z.array(campaignNavigationCategorySchema) })),

  listFilter: oc
    .route({ method: 'GET', path: '/donation-campaigns/list-filter' })
    .errors(guarded)
    .input(z.object({}))
    .output(donationCampaignFilterSchema),

  queueFilter: oc
    .route({ method: 'GET', path: '/donation-campaigns/queue-filter' })
    .errors(guarded)
    .input(z.object({}))
    .output(donationCampaignFilterSchema),

  trashFilter: oc
    .route({ method: 'GET', path: '/donation-campaigns/trash-filter' })
    .errors(guarded)
    .input(z.object({}))
    .output(donationCampaignFilterSchema),
};
